import { AddCircleOutlined, Check, Delete } from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import {
	Box,
	Button,
	Checkbox,
	FormControl,
	IconButton,
	Input,
	ListItemText,
	MenuItem,
	OutlinedInput,
	Paper,
	Select,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	ToggleButton,
	Typography,
} from "@mui/material";
import { DateTime } from "luxon";
import { type Dispatch, type FC, memo, type SetStateAction, useCallback, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { EditTimePopover } from "../../../../components/EditTimePopover";
import { type DropdownItem, SystemDropdown } from "../../../../components/UIToolkit/Inputs/SystemDropDown";
import { ThemedSvg } from "../../../../components/UIToolkit/ThemedSvg";
import { useChosenDepartment } from "../../../../contexts/ChosenDepartment";
import { useDepartmentFlags } from "../../../../hooks/useDepartmentFlags";
import { useDepartmentNurseTypes } from "../../../../hooks/useDepartmentNurseTypes";
import { useRoleColors } from "../../../../hooks/useRoleColors";
import { trackEvent } from "../../../../utils/analytics";
import { type DepartmentLocation, NurseFullRole, NurseFullRoleShort, ShiftType } from "../../../../utils/models";
import { DEFAULT_DEPARTMENT_LOCATION, WEEKDAYS } from "../utils";
import { useDeleteDepartmentLocations } from "./hooks";

type DepartmentLocationTableRowProps = {
	departmentLocation: DepartmentLocation;
	setDepartmentLocations: Dispatch<SetStateAction<DepartmentLocation[]>>;
	setModifiedDepartmentLocations: Dispatch<SetStateAction<Set<string>>>;
	setNewDepartmentLocations: Dispatch<SetStateAction<DepartmentLocation[]>>;
	index: number;
	disabled: boolean;
	isNewRecord: boolean;
	// Department-wide values, read once by DepartmentLocations rather than in every row.
	departmentNurseTypes: NurseFullRole[];
	timezone: string;
	shiftTypes: ShiftType[];
};
type UpdateDeptFn = <TKey extends keyof DepartmentLocation, TValue extends DepartmentLocation[TKey] extends boolean ? [] : [DepartmentLocation[TKey]]>(
	fieldName: TKey,
	...[value]: TValue
) => void;
interface DayToggleProps {
	day: string;
	departmentLocation: DepartmentLocation;
	updateDepartmentLocationField: UpdateDeptFn;
	disabled?: boolean;
}
const DayToggle = ({ day, departmentLocation, updateDepartmentLocationField, disabled }: DayToggleProps) => {
	const currentlySelected = departmentLocation.days?.includes(day);
	const handleToggle = useCallback(() => {
		if (currentlySelected) {
			updateDepartmentLocationField(
				"days",
				departmentLocation.days.filter((d) => d !== day),
			);
			const newDayAmounts = { ...departmentLocation.dayAmounts };
			delete newDayAmounts[day];
			updateDepartmentLocationField("dayAmounts", newDayAmounts);
		} else {
			updateDepartmentLocationField("days", [...(departmentLocation.days || []), day]);
			updateDepartmentLocationField("dayAmounts", {
				...departmentLocation.dayAmounts,
				[day]: { amount: departmentLocation.amount ?? 1, amount_required: departmentLocation.amountRequired ?? 1 },
			});
		}
	}, [day, departmentLocation, currentlySelected, updateDepartmentLocationField]);
	return (
		<IconButton disabled={disabled} onClick={handleToggle} sx={{ width: "12px", height: "12px" }}>
			{currentlySelected ? <img src={"/check.svg"} alt="check" /> : <ThemedSvg src="/uncheck.svg" alt="uncheck" color="var(--inhouse-primary)" />}
		</IconButton>
	);
};
export const DepartmentLocationTableRow = memo<DepartmentLocationTableRowProps>(
	({
		setDepartmentLocations,
		departmentLocation,
		setModifiedDepartmentLocations,
		setNewDepartmentLocations,
		index,
		disabled,
		isNewRecord,
		departmentNurseTypes,
		timezone,
		shiftTypes,
	}) => {
		const roleColor = useRoleColors();
		const updateDepartmentLocationField = useCallback<UpdateDeptFn>(
			(fieldName, ...[value]) => {
				trackEvent(`Department location - update location field ${fieldName}`, {
					department_id: departmentLocation.department_id,
					location_id: departmentLocation.id,
					field_name: fieldName,
					value: value,
				});
				setDepartmentLocations((prev) => {
					const newValue = prev[index] && typeof prev[index][fieldName] === "boolean" ? !prev[index][fieldName] : value;
					prev[index] = { ...prev[index], [fieldName]: newValue };
					return [...prev];
				});
				setModifiedDepartmentLocations((prev) => new Set([...prev, departmentLocation.id]));
			},
			[departmentLocation, setDepartmentLocations, setModifiedDepartmentLocations, index],
		);

		const { mutateAsync: deleteDepartmentLocation } = useDeleteDepartmentLocations({
			id: departmentLocation.id,
		});
		const [anchorRef, setAnchorRef] = useState<HTMLElement>();
		const [isEditingTime, setIsEditingTime] = useState<boolean>(false);
		return (
			<TableRow>
				<TableCell sx={{ padding: "0 8px" }}>
					<Input
						disableUnderline={true}
						value={departmentLocation.section || ""}
						onChange={(event) => {
							updateDepartmentLocationField("section", event.target.value.trim());
						}}
						disabled={disabled}
					/>
				</TableCell>
				<TableCell sx={{ padding: "0 8px" }}>
					<Input
						disableUnderline={true}
						value={departmentLocation.location || ""}
						onChange={(event) => {
							updateDepartmentLocationField("location", event.target.value.trim());
						}}
						disabled={disabled}
					/>
				</TableCell>
				<TableCell sx={{ padding: "0 8px" }}>
					<Input
						disableUnderline={true}
						value={departmentLocation.location_short_name || ""}
						onChange={(event) => {
							updateDepartmentLocationField("location_short_name", event.target.value.trim());
						}}
						disabled={disabled}
					/>
				</TableCell>
				<TableCell sx={{ padding: "0 8px" }}>
					<Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
						<FormControl size="small" sx={{ minWidth: 140, marginRight: "auto", width: "100%" }}>
							<SystemDropdown
								fullWidth
								multiple
								items={
									(departmentNurseTypes
										?.map((role) => role.match(/\((.*?)\)/)?.[1] || role)
										.map((role) => {
											return {
												value: NurseFullRole[role as keyof typeof NurseFullRole],
												label: role,
												color: roleColor(NurseFullRole[role as keyof typeof NurseFullRole]).dot,
											};
										}) as DropdownItem<NurseFullRole>[]) ?? []
								}
								selectedItems={departmentLocation.roles?.map((role) => role as NurseFullRole) ?? []}
								onChangeMultiple={(selectedValues) =>
									updateDepartmentLocationField(
										"roles",
										selectedValues.map((role) => role as NurseFullRole),
									)
								}
							/>
						</FormControl>
					</Box>
				</TableCell>
				<TableCell align={"center"}>
					<ToggleButton
						value="check"
						selected={!!departmentLocation.is_charge}
						onChange={() => {
							updateDepartmentLocationField("is_charge");
						}}
						sx={{ width: "12px", height: "12px" }}
						disabled={disabled}
					>
						{departmentLocation.is_charge && <Check />}
					</ToggleButton>
				</TableCell>
				<TableCell sx={{ padding: "0 8px", width: "100px" }}>
					<Input
						type="number"
						disableUnderline={true}
						value={departmentLocation.amount !== null && departmentLocation.amount !== undefined ? departmentLocation.amount : ""}
						onChange={(event) => {
							const newAmount = +(+event.target.value).toFixed();
							if (+event.target.value >= 0) {
								const newDayAmounts = { ...departmentLocation.dayAmounts };
								for (const day of departmentLocation.days) {
									const existing = newDayAmounts[day] ?? { amount: 0, amount_required: 0 };
									newDayAmounts[day] = {
										amount: newAmount,
										amount_required: Math.min(existing.amount_required, newAmount),
									};
								}
								updateDepartmentLocationField("dayAmounts", newDayAmounts);
							}
						}}
						disabled={disabled}
					/>
				</TableCell>
				<TableCell sx={{ padding: "0 8px", width: "100px" }}>
					<Input
						type="number"
						disableUnderline={true}
						value={departmentLocation.amountRequired ?? ""}
						onChange={(event) => {
							const newRequired = +(+event.target.value).toFixed();
							if (+event.target.value >= 0 && newRequired <= departmentLocation.amount) {
								const newDayAmounts = { ...departmentLocation.dayAmounts };
								for (const day of departmentLocation.days) {
									const existing = newDayAmounts[day] ?? { amount: 1, amount_required: 0 };
									newDayAmounts[day] = { ...existing, amount_required: newRequired };
								}
								updateDepartmentLocationField("dayAmounts", newDayAmounts);
							}
						}}
						disabled={disabled}
					/>
				</TableCell>
				<TableCell sx={{ padding: "0 8px" }}>
					<Box
						sx={{
							alignItems: "center",
							gap: 1,
							color: "primary.dark",
							opacity: 1,
							position: "relative",
							"&:hover .edit-icon": {
								opacity: 1,
							},
							display: "flex",
							paddingRight: "16px",
						}}
					>
						<Typography sx={{ fontSize: "14px" }}>
							{DateTime.fromFormat(departmentLocation.shift_start.substring(0, 5), "HH:mm").toFormat("h:mm a")} -{" "}
							{DateTime.fromFormat(departmentLocation.shift_end.substring(0, 5), "HH:mm").toFormat("h:mm a")}{" "}
						</Typography>
						<IconButton
							disabled={disabled}
							className="edit-icon"
							size="small"
							onClick={(e) => {
								e.stopPropagation();
								setAnchorRef(e.currentTarget);
								setIsEditingTime(true);
							}}
							sx={{
								opacity: 0,
								transition: "opacity 0.2s",
								padding: "2px",
								marginLeft: "4px",
							}}
						>
							{!disabled && <EditIcon sx={{ fontSize: "1rem" }} />}
						</IconButton>
					</Box>
					{isEditingTime && (
						<EditTimePopover
							startTime={DateTime.fromFormat(departmentLocation.shift_start, "HH:mm", { zone: timezone })}
							endTime={DateTime.fromFormat(departmentLocation.shift_end, "HH:mm", { zone: timezone })}
							onTimeChange={({ newStartTime, newEndTime }) => {
								updateDepartmentLocationField("shift_start", newStartTime.toFormat("HH:mm"));
								updateDepartmentLocationField("shift_end", newEndTime.toFormat("HH:mm"));
								setIsEditingTime(false);
							}}
							onClose={() => setIsEditingTime(false)}
							anchorEl={anchorRef || null}
							isActual={false}
							justUpdateOnSave={true}
						/>
					)}
				</TableCell>
				<TableCell sx={{ padding: "0 8px" }} align={"center"}>
					<IconButton
						disabled={disabled}
						onClick={() =>
							updateDepartmentLocationField("shift_type", shiftTypes[(shiftTypes.indexOf(departmentLocation.shift_type as ShiftType) + 1) % shiftTypes.length])
						}
					>
						{departmentLocation.shift_type === ShiftType.Day || departmentLocation.shift_type === ShiftType.Morning ? (
							<img src="/sun.svg" alt="Day Shift" style={{ width: "20px", height: "20px" }} />
						) : (
							<img src="/moon.svg" alt="Night Shift" style={{ width: "20px", height: "20px" }} />
						)}
					</IconButton>
				</TableCell>
				<TableCell>
					<Box
						sx={{
							display: "flex",
							gap: 1,
							justifyContent: "space-around",
						}}
					>
						{WEEKDAYS.map((day) => (
							<DayToggle key={day} day={day} departmentLocation={departmentLocation} updateDepartmentLocationField={updateDepartmentLocationField} />
						))}
					</Box>
				</TableCell>
				<TableCell>
					<Button
						disabled={disabled}
						onClick={() => {
							!isNewRecord
								? deleteDepartmentLocation()
								: (() => {
										setNewDepartmentLocations((prev) => prev.filter((loc) => loc.id !== departmentLocation.id));
										setModifiedDepartmentLocations((prev) => {
											const newSet = new Set(prev);
											newSet.delete(departmentLocation.id);
											return newSet;
										});
									})();
						}}
					>
						<Delete />
					</Button>
				</TableCell>
			</TableRow>
		);
	},
);

type DepartmentLocationsProps = {
	existingDepartmentLocations: DepartmentLocation[];
	setExistingDepartmentLocations: Dispatch<SetStateAction<DepartmentLocation[]>>;
	newDepartmentLocations: DepartmentLocation[];
	setNewDepartmentLocations: Dispatch<SetStateAction<DepartmentLocation[]>>;
	setModifiedDepartmentLocations: Dispatch<SetStateAction<Set<string>>>;
	isUpdatingDepartmentLocations: boolean;
};

export const DepartmentLocations: FC<DepartmentLocationsProps> = ({
	existingDepartmentLocations,
	setExistingDepartmentLocations,
	newDepartmentLocations,
	setNewDepartmentLocations,
	setModifiedDepartmentLocations,
	isUpdatingDepartmentLocations,
}) => {
	const { chosenDepartment } = useChosenDepartment();
	const { timezone } = useDepartmentFlags();
	const departmentNurseTypes = useDepartmentNurseTypes(chosenDepartment.department_id);
	const addNewRow = useCallback(() => {
		setNewDepartmentLocations((prev) => [
			{
				...DEFAULT_DEPARTMENT_LOCATION,
				department_id: chosenDepartment.department_id,
				id: uuidv4(),
				roles: departmentNurseTypes,
			},
			...prev,
		]);
	}, [setNewDepartmentLocations, chosenDepartment.department_id, departmentNurseTypes]);

	return (
		<Box
			component="section"
			sx={{
				display: "flex",
				flexWrap: "wrap",
				alignItems: "center",
				gap: "30px",
			}}
		>
			<Box
				sx={{
					flex: "1 1 100%",
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
				}}
			>
				<Box component="h2" sx={{ fontSize: "20px", fontWeight: 600, color: "primary.main" }}>
					Locations
				</Box>
				<Button onClick={addNewRow}>
					<AddCircleOutlined />
					<Box component="p" sx={{ px: "8px" }}>
						Add New
					</Box>
				</Button>
			</Box>
			<TableContainer component={Paper} sx={{ height: "max-content", flex: "1 1 100%" }}>
				<Table aria-label="Department Locations">
					<TableHead sx={{ backgroundColor: "primary.main" }}>
						<TableRow>
							<TableCell sx={{ color: "#ffffff", px: 1 }}>Section</TableCell>
							<TableCell sx={{ color: "#ffffff" }}>Name</TableCell>
							<TableCell sx={{ color: "#ffffff" }}>Nick Name</TableCell>
							<TableCell sx={{ color: "#ffffff" }}>Roles</TableCell>
							<TableCell sx={{ color: "#ffffff", px: 1 }}>Charge</TableCell>
							<TableCell sx={{ color: "#ffffff" }}>Amount</TableCell>
							<TableCell sx={{ color: "#ffffff" }}>Amount Required</TableCell>
							<TableCell sx={{ color: "#ffffff" }}>Shift Hours</TableCell>
							<TableCell sx={{ color: "#ffffff", px: 1 }} align={"center"}>
								Shift Type
							</TableCell>
							<TableCell sx={{ color: "#ffffff" }}>
								<Box
									sx={{
										display: "flex",
										gap: 1,
										justifyContent: "space-around",
									}}
								>
									{WEEKDAYS.map((day) => (
										<Typography key={day}>{day.charAt(0).toUpperCase() + day.slice(1)}</Typography>
									))}
								</Box>
							</TableCell>

							<TableCell sx={{ color: "#ffffff" }} align={"center"}>
								Actions
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{newDepartmentLocations?.map(
							(newDepartmentLocation, index) =>
								newDepartmentLocation && (
									<DepartmentLocationTableRow
										setDepartmentLocations={setNewDepartmentLocations}
										key={`new-${index.toString()}`}
										index={index}
										departmentLocation={newDepartmentLocation}
										disabled={isUpdatingDepartmentLocations}
										setModifiedDepartmentLocations={setModifiedDepartmentLocations}
										setNewDepartmentLocations={setNewDepartmentLocations}
										isNewRecord={true}
										departmentNurseTypes={departmentNurseTypes}
										timezone={timezone}
										shiftTypes={chosenDepartment.shift_types}
									/>
								),
						)}
						{existingDepartmentLocations?.map(
							(existingDepartmentLocation, index) =>
								existingDepartmentLocation && (
									<DepartmentLocationTableRow
										setDepartmentLocations={setExistingDepartmentLocations}
										index={index}
										key={`existing-${index.toString()}`}
										departmentLocation={existingDepartmentLocation}
										disabled={isUpdatingDepartmentLocations}
										setModifiedDepartmentLocations={setModifiedDepartmentLocations}
										setNewDepartmentLocations={setNewDepartmentLocations}
										isNewRecord={false}
										departmentNurseTypes={departmentNurseTypes}
										timezone={timezone}
										shiftTypes={chosenDepartment.shift_types}
									/>
								),
						)}
					</TableBody>
				</Table>
			</TableContainer>
		</Box>
	);
};
